export enum AppStorageKey {
  Token = 'qhrms_token',
  RefreshToken = 'qhrms_refresh_token',
  User = 'qhrms_user',
  Theme = 'qhrms_theme',
  SidebarCollapsed = 'qhrms_sidebar_collapsed',
}

export enum DateFormat {
  Display = 'dd MMM yyyy',
  Input = 'yyyy-MM-dd',
  DisplayWithTime = 'dd MMM yyyy HH:mm',
  MonthYear = 'MMMM yyyy',
  ShortMonth = 'MMM yyyy',
}

export enum EmploymentType {
  FullTime = 'Full Time',
  PartTime = 'Part Time',
  Contract = 'Contract',
  Internship = 'Internship',
  Consultant = 'Consultant',
  Apprenticeship = 'Apprenticeship',
  Labor = 'Labor',
  Freelancer = 'Freelancer',
}

export enum EmploymentStatus {
  OnProbation = 'On Probation',
  Confirmed = 'Confirmed',
  Resigned = 'Resigned',
  Terminated = 'Terminated',
  Inactive = 'Inactive',
}

export enum TicketPriority {
  Low = 'Low',
  Medium = 'Medium',
  High = 'High',
  Critical = 'Critical',
}

export enum TicketStatus {
  Open = 'Open',
  Assigned = 'Assigned',
  InProgress = 'InProgress',
  Resolved = 'Resolved',
  Closed = 'Closed',
}

export enum LeaveDayPart {
  FullDay = 'FullDay',
  FirstHalf = 'FirstHalf',
  SecondHalf = 'SecondHalf',
}

import {
  ROLES as ROLE_CODES,
  ROLE_NAMES,
  ROLE_IDS,
  ROLE_LABELS,
  ROLE_COLORS,
  ROLE_DEFINITIONS,
  QHRMS_ROLE_DEFINITIONS,
  getRoleName,
  type ROLES as RoleCode,
  type RoleKey,
  type RoleType,
  type RoleDefinition,
} from './roles';

export const ROLES = ROLE_CODES;
export type ROLES = RoleCode;

export {
  ROLE_NAMES,
  ROLE_IDS,
  ROLE_LABELS,
  ROLE_COLORS,
  ROLE_DEFINITIONS,
  QHRMS_ROLE_DEFINITIONS,
  getRoleName,
  type RoleKey,
  type RoleType,
  type RoleDefinition,
};

export enum HolidayTypeId {
  WeeklyOff = 4,
  Custom = 5,
  Optional = 6,
}

/** DB names in qhrms.HolidayTypes - use for resolving typeId from GET /holiday/types. */
export const HOLIDAY_TYPE_NAME_BY_KEY = {
  WEEKLY_OFF: 'WeekEnd',
  CUSTOM: 'Custom',
  OPTIONAL: 'Optional',
} as const;

export enum RoutePath {
  Login = '/login',
  Unauthorized = '/unauthorized',
  NotFound = '/404',
  MySpace = '/my-space',
  Profile = '/profile',
  Leave = '/leave',
  Attendance = '/attendance',
  Holidays = '/holidays',
  Payroll = '/payroll',
  Performance = '/performance',
  Requests = '/requests',
  Helpdesk = '/helpdesk',
  ReqCentre = '/req-centre',
  Hierarchy = '/hierarchy',
  OpsEmployees = '/ops/employees',
  OpsPayroll = '/ops/payroll',
  OpsLeaveLedger = '/ops/leave-ledger',
  OpsLetters = '/ops/letters',
  OpsAssets = '/ops/assets',
  OpsPerformance = '/ops/performance',
  AdminDashboard = '/admin/dashboard',
  ConfigOrg = '/config/org',
  ConfigHolidayCalendar = '/config/holiday-calendar',
  ConfigEmpData = '/config/empdata',
  ConfigLeave = '/config/leave',
  ConfigPayroll = '/config/payroll',
  ConfigPerformance = '/config/performance',
  ConfigAssets = '/config/assets',
  ConfigHelpdesk = '/config/helpdesk',
  ConfigLetters = '/config/letters',
  ConfigNotices = '/config/notices',
}

export enum ApiPath {
  // AuthLogin = '/auth/login', // disabled - Microsoft SSO only
  AuthSsoLogin = '/auth/sso-login',
  AuthMe = '/auth/me',
  AuthActiveOrganization = '/auth/active-organization',
  AuthRefresh = '/auth/refresh',
  AuthForgotPassword = '/auth/forgot-password',
  AuthResetPassword = '/auth/reset-password',
  AuthOnboarding = '/auth/onboarding',
  EmployeesList = '/employees',
  EmployeesStats = '/employees/stats',
  EmployeesInvite = '/employees/invite',
  EmployeesBulkInvite = '/employees/bulk-invite',
  EmployeesSearch = '/employees/search',
  EmployeesRoles = '/employees/roles',
  EntraUsers = '/entra/users',
  EntraImport = '/entra/import',
  OrganizationAll = '/Organization/all',
  OrganizationCreate = '/Organization',
  OrganizationDetails = '/Organization/details',
  OrganizationGet = '/organization',
  OrganizationLocation = '/Organization/locations',
  OrganizationGlobal = '/organization/global',
  OrganizationIncorporation = '/organization/incorporation',
  OrganizationBranding = '/organization/branding',
  OrganizationUiConfig = '/organization/ui-config',
  OrganizationBrandingFavicon = '/organization/branding/favicon',
  OrganizationLogo = '/organization/logo',
  OrganizationBusinessUnits = '/organization/business-units',
  OrganizationDepartments = '/organization/departments',
  OrganizationDesignations = '/organization/designations',
  OrganizationGrades = '/organization/grades',
  OrganizationLocations = '/organization/locations',
  OrganizationEmailSettings = '/organization/email-settings',
  LeaveTypes = '/leave/types',
  LeaveBalances = '/leave/balances',
  LeaveApplications = '/leave/applications',
  LeavePending = '/leave/applications/pending',
  LeaveApply = '/leave/apply',
  LeaveApprove = '/leave/approve',
  LeaveManualAdjust = '/leave/adjust',
  LeaveCarryForward = '/leave/carry-forward',
  LeaveConfigBase = '/leave-config',
  HolidayMy = '/holiday/my',
  HolidayPlans = '/holiday/plans',
  HolidayTypes = '/holiday/types',
  HolidayAssign = '/holiday/assign',
  AttendanceMy = '/attendance/my',
  AttendanceSummary = '/attendance/summary',
  AttendanceSubmit = '/attendance/submit',
  AttendancePending = '/attendance/pending',
  AttendanceShifts = '/attendance/shifts',
  AttendanceWorkModes = '/attendance/work-modes',
  PayrollMyPackage = '/payroll/my/package',
  PayrollMyPackages = '/payroll/my/packages',
  PayrollMyPayslips = '/payroll/my/payslips',
  PayrollMyTaxSheet = '/payroll/my/tax-sheet',
  PayrollRuns = '/payroll/runs',
  PayrollInitiate = '/payroll/initiate',
  PayrollConfig = '/payroll/config',
  PayrollSalaryStructures = '/payroll/salary-structures',
  PayrollComponents = '/payroll/components',
  PayrollPfOptions = '/payroll/pf-options',
  PayrollTaxSlabs = '/payroll/tax-slabs',
  PayrollAdvances = '/payroll/advances',
  PayrollLoans = '/payroll/loans',
  PayrollExtraPayments = '/payroll/extra-payments',
  PayrollExtraDeductions = '/payroll/extra-deductions',
  HelpdeskCategories = '/helpdesk/categories',
  HelpdeskMyTickets = '/helpdesk/my-tickets',
  HelpdeskAllTickets = '/helpdesk/all-tickets',
  HelpdeskCreate = '/helpdesk',
  HelpdeskAssign = '/helpdesk/assign',
  HelpdeskResolve = '/helpdesk/resolve',
  HelpdeskComment = '/helpdesk/comment',
  LettersTypes = '/letters/types',
  LettersList = '/letters',
  LettersMy = '/letters/my',
  LettersGenerate = '/letters/generate',
  LettersLetterheads = '/letters/letterheads',
  PerformanceConfig = '/performance/config',
  PerformanceFocusAreas = '/performance/focus-areas',
  PerformanceForms = '/performance/forms',
  PerformanceMyAssignments = '/performance/assignments/my',
  PerformanceAllAssignments = '/performance/assignments',
  PerformanceAssign = '/performance/assign',
  PerformanceSelfEval = '/performance/self-eval',
  PerformanceManagerEval = '/performance/manager-eval',
  PerformanceMyResults = '/performance/results/my',
  RequestsWfh = '/requests/wfh',
  RequestsWfhMy = '/requests/wfh/my',
  RequestsWfhPending = '/requests/wfh/pending',
  RequestsCompOff = '/requests/compoff',
  RequestsCompOffMy = '/requests/compoff/my',
  RequestsCompOffPending = '/requests/compoff/pending',
  RequestsResignation = '/requests/resignation',
  RequestsResignationMy = '/requests/resignation/my',
  RequestsResignationPending = '/requests/resignation/pending',
  RequestsPendingCount = '/requests/pending-count',
  RequestsLeaveTypes = '/requests/leave-types',
  RequestsExitReasons = '/requests/exit-reasons',
  MasterSalutations = '/master/salutations',
  MasterGenders = '/master/genders',
  MasterGenderPronouns = '/master/gender-pronouns',
  MasterMaritalStatuses = '/master/marital-statuses',
  MasterDocumentCategories = '/master/document-categories',
  MasterDocumentTypes = '/master/document-types',
  MasterEmployeeTypes = '/master/employee-types',
  MasterEmployeeStages = '/master/employee-stages',
  MasterCountries = '/master/countries',
  MasterStates = '/master/states',
  DashboardMySpace = '/dashboard/my-space',
  DashboardAnnouncements = '/dashboard/announcements',
  DashboardNotifications = '/dashboard/notifications',
  DashboardReadAll = '/dashboard/notifications/read-all',
  DashboardOnLeave = '/dashboard/on-leave',
  DashboardJoinees = '/dashboard/joinees',
  DashboardCelebrations = '/dashboard/celebrations',
  AnnouncementsBase = '/announcements',
  AnnouncementsAdmin = '/announcements/admin',
  AssetsBase = '/assets',
  AssetsTypes = '/assets/types',
  AssetsBulk = '/assets/bulk',
  EmployeeDataConfigId = '/config/employee-data/id-config',
  EmployeeDataConfigProbation = '/config/employee-data/probation-plans',
  EmployeeDataConfigNotice = '/config/employee-data/notice-plans',
  EmployeeDataConfigExitReasons = '/config/employee-data/exit-reasons',
  EmployeeDataConfigDocumentTypes = '/config/employee-data/document-types',
  EmployeeDataConfigDocumentCategories = '/config/employee-data/document-categories',
  EmployeeDataConfigEmployeeTypes = '/config/employee-data/employee-types',
  EmployeeDataConfigEmployeeStages = '/config/employee-data/employee-stages',
}

export const APP_CONSTANTS = {
  APP_NAME: 'QHRMS',
  TOKEN_KEY: AppStorageKey.Token,
  REFRESH_TOKEN_KEY: AppStorageKey.RefreshToken,
  USER_KEY: AppStorageKey.User,
  THEME_KEY: AppStorageKey.Theme,
  SIDEBAR_KEY: AppStorageKey.SidebarCollapsed,

  PAGINATION: {
    DEFAULT_PAGE: 1,
    DEFAULT_PAGE_SIZE: 20,
    PAGE_SIZE_OPTIONS: [10, 20, 50, 100],
  },

  DATE_FORMATS: {
    DISPLAY: DateFormat.Display,
    INPUT: DateFormat.Input,
    DISPLAY_WITH_TIME: DateFormat.DisplayWithTime,
    MONTH_YEAR: DateFormat.MonthYear,
    SHORT_MONTH: DateFormat.ShortMonth,
  },

  EMPLOYMENT_TYPES: Object.values(EmploymentType),
  EMPLOYMENT_STATUSES: Object.values(EmploymentStatus),

  TICKET_PRIORITIES: Object.values(TicketPriority),
  TICKET_STATUSES: Object.values(TicketStatus),

  LEAVE_DAY_PARTS: Object.values(LeaveDayPart),

  BANK_EDIT_WINDOW: { START_DAY: 21, END_DAY: 31 },
} as const;

/** Master organization list for header org switcher */
export const ORGANIZATION_IDS = {
  AKUMEN: '58497AEE-8A87-44DB-822F-B316B8DB36F8',
  QUBIQON: 'CEA95265-0690-4A6D-9251-E12E48630C60',
  PM_TOOL_DEMO: 'EC1F0C72-8AFD-59EB-9A6E-F2335EEFDE10',
} as const;

export type OrganizationOption = {
  id: (typeof ORGANIZATION_IDS)[keyof typeof ORGANIZATION_IDS];
  name: string;
};

export const ORGANIZATIONS: readonly OrganizationOption[] = [
  { id: ORGANIZATION_IDS.AKUMEN, name: 'akumen' },
  { id: ORGANIZATION_IDS.QUBIQON, name: 'Qubiqon Consulting India Private Limited' },
  { id: ORGANIZATION_IDS.PM_TOOL_DEMO, name: 'PM Tool Demo Organization' },
] as const;

export const DEFAULT_ORGANIZATION_ID = ORGANIZATION_IDS.QUBIQON;

export const DEFAULT_ORGANIZATION =
  ORGANIZATIONS.find((o) => o.id === DEFAULT_ORGANIZATION_ID) ?? ORGANIZATIONS[1];

export function pickDefaultOrganization(
  list: readonly { id: string; name: string }[],
): { id: string; name: string } {
  const match = list.find(
    (o) => o.id.toLowerCase() === DEFAULT_ORGANIZATION_ID.toLowerCase(),
  );
  return match ?? DEFAULT_ORGANIZATION;
}

export const PERMISSIONS = {
  VIEW_OWN_PROFILE: [ROLES.ADMIN, ROLES.EMPLOYEE, ROLES.HR, ROLES.FINANCE, ROLES.APPROVER],
  EDIT_OWN_PROFILE: [ROLES.ADMIN, ROLES.EMPLOYEE, ROLES.HR, ROLES.FINANCE, ROLES.APPROVER],
  VIEW_ALL_EMPLOYEES: [ROLES.ADMIN, ROLES.HR],
  MANAGE_EMPLOYEES: [ROLES.ADMIN, ROLES.HR],

  APPLY_LEAVE: [ROLES.ADMIN, ROLES.EMPLOYEE, ROLES.HR, ROLES.FINANCE, ROLES.APPROVER],
  APPROVE_LEAVE: [ROLES.ADMIN, ROLES.HR, ROLES.APPROVER],
  VIEW_LEAVE_LEDGER: [ROLES.ADMIN, ROLES.HR],

  VIEW_OWN_PAYROLL: [ROLES.ADMIN, ROLES.EMPLOYEE, ROLES.HR, ROLES.FINANCE, ROLES.APPROVER],
  MANAGE_PAYROLL: [ROLES.ADMIN, ROLES.FINANCE],
  RUN_PAYROLL: [ROLES.ADMIN, ROLES.FINANCE],

  VIEW_OWN_PERFORMANCE: [ROLES.ADMIN, ROLES.EMPLOYEE, ROLES.HR, ROLES.FINANCE, ROLES.APPROVER],
  MANAGE_PERFORMANCE: [ROLES.ADMIN, ROLES.HR],

  ACCESS_CONFIG: [ROLES.ADMIN],
  MANAGE_ORG_SETTINGS: [ROLES.ADMIN],

  CREATE_TICKET: [ROLES.ADMIN, ROLES.EMPLOYEE, ROLES.HR, ROLES.FINANCE, ROLES.APPROVER],
  MANAGE_TICKETS: [ROLES.ADMIN, ROLES.HR],

  VIEW_ADMIN_DASHBOARD: [ROLES.ADMIN],
  VIEW_HR_DASHBOARD: [ROLES.ADMIN, ROLES.HR],
  VIEW_FINANCE_DASHBOARD: [ROLES.ADMIN, ROLES.FINANCE],
} as const;

export type PermissionKey = keyof typeof PERMISSIONS;

export const DEFAULT_HOLIDAY_CALENDAR_NAME = 'Company Holiday Calendar';

export const HOLIDAY_TYPE_IDS = {
  OPTIONAL: HolidayTypeId.Optional,
  CUSTOM: HolidayTypeId.Custom,
  WEEKLY_OFF: HolidayTypeId.WeeklyOff,
} as const;

export const HOLIDAY_TYPE_NAMES: Record<HolidayTypeId, string> = {
  [HolidayTypeId.WeeklyOff]: HOLIDAY_TYPE_NAME_BY_KEY.WEEKLY_OFF,
  [HolidayTypeId.Custom]: HOLIDAY_TYPE_NAME_BY_KEY.CUSTOM,
  [HolidayTypeId.Optional]: HOLIDAY_TYPE_NAME_BY_KEY.OPTIONAL,
};

/** Employee holidays calendar - Custom (5) = mandatory, Optional (6) = optional. */
export const HOLIDAY_CALENDAR_LEGEND = {
  mandatory: {
    typeId: HolidayTypeId.Custom,
    label: 'Mandatory holiday',
    color: '#5591F3',
    textClass: 'text-white',
  },
  optional: {
    typeId: HolidayTypeId.Optional,
    label: 'Optional holiday',
    color: '#BCE2E0',
    textClass: 'text-brand-navy',
  },
} as const;

export const ROUTE_PATHS = {
  LOGIN: RoutePath.Login,
  UNAUTHORIZED: RoutePath.Unauthorized,
  NOT_FOUND: RoutePath.NotFound,
  MY_SPACE: RoutePath.MySpace,
  PROFILE: RoutePath.Profile,
  LEAVE: RoutePath.Leave,
  ATTENDANCE: RoutePath.Attendance,
  HOLIDAYS: RoutePath.Holidays,
  PAYROLL: RoutePath.Payroll,
  PERFORMANCE: RoutePath.Performance,
  REQUESTS: RoutePath.Requests,
  HELPDESK: RoutePath.Helpdesk,
  REQ_CENTRE: RoutePath.ReqCentre,
  HIERARCHY: RoutePath.Hierarchy,
  OPS_EMPLOYEES: RoutePath.OpsEmployees,
  OPS_PAYROLL: RoutePath.OpsPayroll,
  OPS_LEAVE_LEDGER: RoutePath.OpsLeaveLedger,
  OPS_LETTERS: RoutePath.OpsLetters,
  OPS_ASSETS: RoutePath.OpsAssets,
  OPS_PERFORMANCE: RoutePath.OpsPerformance,
  ADMIN_DASHBOARD: RoutePath.AdminDashboard,
  CONFIG_ORG: RoutePath.ConfigOrg,
  CONFIG_HOLIDAY_CALENDAR: RoutePath.ConfigHolidayCalendar,
  CONFIG_EMPDATA: RoutePath.ConfigEmpData,
  CONFIG_LEAVE: RoutePath.ConfigLeave,
  CONFIG_PAYROLL: RoutePath.ConfigPayroll,
  CONFIG_PERFORMANCE: RoutePath.ConfigPerformance,
  CONFIG_ASSETS: RoutePath.ConfigAssets,
  CONFIG_HELPDESK: RoutePath.ConfigHelpdesk,
  CONFIG_LETTERS: RoutePath.ConfigLetters,
  CONFIG_NOTICES: RoutePath.ConfigNotices,
} as const;

export const API_ENDPOINTS = {
  AUTH: {
    // LOGIN: ApiPath.AuthLogin, // disabled - Microsoft SSO only
    SSO_LOGIN: ApiPath.AuthSsoLogin,
    ME: ApiPath.AuthMe,
    ACTIVE_ORGANIZATION: ApiPath.AuthActiveOrganization,
    REFRESH: ApiPath.AuthRefresh,
    FORGOT_PASSWORD: ApiPath.AuthForgotPassword,
    RESET_PASSWORD: ApiPath.AuthResetPassword,
    ONBOARDING: ApiPath.AuthOnboarding,
  },

  EMPLOYEES: {
    LIST: ApiPath.EmployeesList,
    STATS: ApiPath.EmployeesStats,
    BY_ID: (id: string) => `/employees/${id}`,
    PERSONAL_DATA: (id: string) => `/employees/${id}/personal-data`,
    WORK_PROFILE: (id: string) => `/employees/${id}/work-profile`,
    ADDITIONAL_DETAILS: (id: string) => `/employees/${id}/additional-details`,
    INVITE: ApiPath.EmployeesInvite,
    BULK_INVITE: ApiPath.EmployeesBulkInvite,
    PERSONAL: (id: number) => `/employees/${id}/personal`,
    ROLE: (id: number) => `/employees/${id}/role`,
    STATUS: (id: number) => `/employees/${id}/status`,
    MANAGER: (id: number) => `/employees/${id}/manager`,
    HIERARCHY: (id: number) => `/employees/${id}/hierarchy`,
      SEARCH: ApiPath.EmployeesSearch,
      ROLES: ApiPath.EmployeesRoles,
    PROFILE_PICTURE: (id: string) => `/employees/${id}/profile-picture`,
    DOCUMENTS: (id: string) => `/employees/${id}/documents`,
    DOCUMENT: (employeeId: string, documentId: string) => `/employees/${employeeId}/documents/${documentId}`,
    ASSETS: (id: string) => `/employees/${id}/assets`,
    ACCESS: (id: string) => `/employees/${id}/access`,
  },

  ENTRA: {
    USERS: ApiPath.EntraUsers,
    IMPORT: ApiPath.EntraImport,
  },

  ORGANIZATION: {
    ALL: ApiPath.OrganizationAll,
    CREATE: ApiPath.OrganizationCreate,
    DETAILS: ApiPath.OrganizationDetails,
    GET: ApiPath.OrganizationGet,
    LOCATION: (id: number) => `${ApiPath.OrganizationLocation}/${id}`,
    GLOBAL: ApiPath.OrganizationGlobal,
    INCORPORATION: ApiPath.OrganizationIncorporation,
    BRANDING: ApiPath.OrganizationBranding,
    UI_CONFIG: ApiPath.OrganizationUiConfig,
    BRANDING_FAVICON: ApiPath.OrganizationBrandingFavicon,
    LOGO: ApiPath.OrganizationLogo,
    BUSINESS_UNITS: ApiPath.OrganizationBusinessUnits,
    DEPARTMENTS: ApiPath.OrganizationDepartments,
    DEPARTMENT: (id: number) => `${ApiPath.OrganizationDepartments}/${id}`,
    DESIGNATIONS: ApiPath.OrganizationDesignations,
    DESIGNATION: (id: number) => `${ApiPath.OrganizationDesignations}/${id}`,
    GRADES: ApiPath.OrganizationGrades,
    GRADE: (id: number) => `${ApiPath.OrganizationGrades}/${id}`,
    LOCATIONS: ApiPath.OrganizationLocations,
    EMAIL_SETTINGS: ApiPath.OrganizationEmailSettings,
  },

  LEAVE: {
    TYPES: ApiPath.LeaveTypes,
    BALANCES: ApiPath.LeaveBalances,
    BALANCES_BY_ID: (id: string) => `/leave/balances/${id}`,
    APPLICATIONS: ApiPath.LeaveApplications,
    PENDING: ApiPath.LeavePending,
    APPLY: ApiPath.LeaveApply,
    APPROVE: ApiPath.LeaveApprove,
    CANCEL: (id: number) => `/leave/cancel/${id}`,
    REVOKE: (id: number) => `/leave/revoke/${id}`,
    LEDGER: (empId: string) => `/leave/ledger/${empId}`,
    MANUAL_ADJUST: ApiPath.LeaveManualAdjust,
    CARRY_FORWARD: ApiPath.LeaveCarryForward,
  },

  LEAVE_CONFIG: {
    BASE: ApiPath.LeaveConfigBase,
    CYCLE: `${ApiPath.LeaveConfigBase}/cycle`,
    CARRY_FORWARD: `${ApiPath.LeaveConfigBase}/cycle/carry-forward`,
    DEFAULT_POLICY: (kind: string) => `${ApiPath.LeaveConfigBase}/default-policy/${kind}`,
    CATEGORIES: `${ApiPath.LeaveConfigBase}/categories`,
    ORGANISATION_LEAVES: `${ApiPath.LeaveConfigBase}/organisation-leaves`,
  },

  HOLIDAY: {
    MY: ApiPath.HolidayMy,
    PLANS: ApiPath.HolidayPlans,
    TYPES: ApiPath.HolidayTypes,
    ASSIGN: ApiPath.HolidayAssign,
  },

  ATTENDANCE: {
    MY: ApiPath.AttendanceMy,
    SUMMARY: ApiPath.AttendanceSummary,
    BY_EMPLOYEE: (id: string) => `/attendance/${id}`,
    SUBMIT: ApiPath.AttendanceSubmit,
    APPROVE: (id: number) => `/attendance/${id}/approve`,
    PENDING: ApiPath.AttendancePending,
    SHIFTS: ApiPath.AttendanceShifts,
    WORK_MODES: ApiPath.AttendanceWorkModes,
  },

  PAYROLL: {
    MY_PACKAGE: ApiPath.PayrollMyPackage,
    MY_PACKAGES: ApiPath.PayrollMyPackages,
    MY_PAYSLIPS: ApiPath.PayrollMyPayslips,
    MY_PAYSLIP: (y: number, m: number) => `/payroll/my/payslips/${y}/${m}`,
    MY_TAX_SHEET: ApiPath.PayrollMyTaxSheet,
    RUNS: ApiPath.PayrollRuns,
    RUN: (y: number, m: number) => `/payroll/runs/${y}/${m}`,
    INITIATE: ApiPath.PayrollInitiate,
    PROCESS: (id: number) => `/payroll/process/${id}`,
    CLOSE: (id: number) => `/payroll/close/${id}`,
    SALARY_SHEET: (id: number) => `/payroll/salary-sheet/${id}`,
    BANK_SHEET: (id: number) => `/payroll/bank-sheet/${id}`,
    CONFIG: ApiPath.PayrollConfig,
    SALARY_STRUCTURES: ApiPath.PayrollSalaryStructures,
    COMPONENTS: ApiPath.PayrollComponents,
    PF_OPTIONS: ApiPath.PayrollPfOptions,
    TAX_SLABS: ApiPath.PayrollTaxSlabs,
    ADVANCES: ApiPath.PayrollAdvances,
    LOANS: ApiPath.PayrollLoans,
    EXTRA_PAYMENTS: ApiPath.PayrollExtraPayments,
    EXTRA_DEDUCTIONS: ApiPath.PayrollExtraDeductions,
  },

  HELPDESK: {
    CATEGORIES: ApiPath.HelpdeskCategories,
    MY_TICKETS: ApiPath.HelpdeskMyTickets,
    ALL_TICKETS: ApiPath.HelpdeskAllTickets,
    BY_ID: (id: number) => `/helpdesk/${id}`,
    CREATE: ApiPath.HelpdeskCreate,
    ASSIGN: ApiPath.HelpdeskAssign,
    RESOLVE: ApiPath.HelpdeskResolve,
    CLOSE: (id: number) => `/helpdesk/close/${id}`,
    COMMENT: ApiPath.HelpdeskComment,
  },

  LETTERS: {
    TYPES: ApiPath.LettersTypes,
    LIST: ApiPath.LettersList,
    MY: ApiPath.LettersMy,
    GENERATE: ApiPath.LettersGenerate,
    DOWNLOAD: (id: number) => `/letters/${id}/download`,
    LETTERHEADS: ApiPath.LettersLetterheads,
  },

  PERFORMANCE: {
    CONFIG: ApiPath.PerformanceConfig,
    FOCUS_AREAS: ApiPath.PerformanceFocusAreas,
    FORMS: ApiPath.PerformanceForms,
    FORM: (id: number) => `/performance/forms/${id}`,
    MY_ASSIGNMENTS: ApiPath.PerformanceMyAssignments,
    ALL_ASSIGNMENTS: ApiPath.PerformanceAllAssignments,
    ASSIGN: ApiPath.PerformanceAssign,
    SELF_EVAL: ApiPath.PerformanceSelfEval,
    MANAGER_EVAL: ApiPath.PerformanceManagerEval,
    HR_REVIEW: (id: number) => `/performance/hr-review/${id}`,
    PUBLISH: (id: number) => `/performance/publish/${id}`,
    MY_RESULTS: ApiPath.PerformanceMyResults,
  },

  REQUESTS: {
    WFH: ApiPath.RequestsWfh,
    WFH_MY: ApiPath.RequestsWfhMy,
    WFH_PENDING: ApiPath.RequestsWfhPending,
    WFH_APPROVE: (id: number) => `/requests/wfh/${id}/approve`,
    COMP_OFF: ApiPath.RequestsCompOff,
    COMP_OFF_MY: ApiPath.RequestsCompOffMy,
    COMP_OFF_PENDING: ApiPath.RequestsCompOffPending,
    COMP_OFF_APPROVE: (id: number) => `/requests/compoff/${id}/approve`,
    RESIGNATION: ApiPath.RequestsResignation,
    RESIGNATION_MY: ApiPath.RequestsResignationMy,
    RESIGNATION_PENDING: ApiPath.RequestsResignationPending,
    RESIGNATION_APPROVE: (id: number) => `/requests/resignation/${id}/approve`,
    PENDING_COUNT: ApiPath.RequestsPendingCount,
    LEAVE_TYPES: ApiPath.RequestsLeaveTypes,
    EXIT_REASONS: ApiPath.RequestsExitReasons,
  },

  MASTER: {
    SALUTATIONS: ApiPath.MasterSalutations,
    GENDERS: ApiPath.MasterGenders,
    GENDER_PRONOUNS: ApiPath.MasterGenderPronouns,
    MARITAL_STATUSES: ApiPath.MasterMaritalStatuses,
    DOCUMENT_CATEGORIES: ApiPath.MasterDocumentCategories,
    DOCUMENT_TYPES: ApiPath.MasterDocumentTypes,
    EMPLOYEE_TYPES: ApiPath.MasterEmployeeTypes,
    EMPLOYEE_STAGES: ApiPath.MasterEmployeeStages,
    COUNTRIES: ApiPath.MasterCountries,
    STATES: (countryId: string) => `${ApiPath.MasterStates}?countryId=${countryId}`,
  },

  DASHBOARD: {
    MY_SPACE: ApiPath.DashboardMySpace,
    ANNOUNCEMENTS: ApiPath.DashboardAnnouncements,
    NOTIFICATIONS: ApiPath.DashboardNotifications,
    READ_ALL: ApiPath.DashboardReadAll,
    READ: (id: number) => `/dashboard/notifications/${id}/read`,
    ON_LEAVE: ApiPath.DashboardOnLeave,
    JOINEES: ApiPath.DashboardJoinees,
    CELEBRATIONS: ApiPath.DashboardCelebrations,
  },

  ANNOUNCEMENTS: {
    BASE: ApiPath.AnnouncementsBase,
    PUBLISHED: ApiPath.AnnouncementsBase,
    ADMIN: ApiPath.AnnouncementsAdmin,
    BY_ID: (id: number) => `${ApiPath.AnnouncementsBase}/${id}`,
    ACKNOWLEDGE: (id: number) => `${ApiPath.AnnouncementsBase}/${id}/acknowledge`,
    REACTION: (id: number) => `${ApiPath.AnnouncementsBase}/${id}/reaction`,
  },

  ASSETS: {
    LIST: ApiPath.AssetsBase,
    BY_ID: (id: number) => `/assets/${id}`,
    TYPES: ApiPath.AssetsTypes,
    TYPE_BY_ID: (id: number) => `/assets/types/${id}`,
    TYPE_FIELDS: (id: number) => `/assets/types/${id}/fields`,
    ASSIGN: (id: number) => `/assets/${id}/assign`,
    RETURN: (id: number) => `/assets/${id}/return`,
    BULK: ApiPath.AssetsBulk,
  },
  EMPLOYEE_DATA_CONFIG: {
    ID_CONFIG: ApiPath.EmployeeDataConfigId,
    PROBATION_PLANS: ApiPath.EmployeeDataConfigProbation,
    PROBATION_PLAN: (id: number) => `${ApiPath.EmployeeDataConfigProbation}/${id}`,
    NOTICE_PLANS: ApiPath.EmployeeDataConfigNotice,
    NOTICE_PLAN: (id: number) => `${ApiPath.EmployeeDataConfigNotice}/${id}`,
    EXIT_REASONS: ApiPath.EmployeeDataConfigExitReasons,
    EXIT_REASON: (id: number) => `${ApiPath.EmployeeDataConfigExitReasons}/${id}`,
    DOCUMENT_TYPES: ApiPath.EmployeeDataConfigDocumentTypes,
    DOCUMENT_TYPE: (id: number) => `${ApiPath.EmployeeDataConfigDocumentTypes}/${id}`,
    DOCUMENT_CATEGORIES: ApiPath.EmployeeDataConfigDocumentCategories,
    DOCUMENT_CATEGORY: (id: number) => `${ApiPath.EmployeeDataConfigDocumentCategories}/${id}`,
    EMPLOYEE_TYPES: ApiPath.EmployeeDataConfigEmployeeTypes,
    EMPLOYEE_TYPE: (id: number) => `${ApiPath.EmployeeDataConfigEmployeeTypes}/${id}`,
    EMPLOYEE_STAGES: ApiPath.EmployeeDataConfigEmployeeStages,
    EMPLOYEE_STAGE: (id: number) => `${ApiPath.EmployeeDataConfigEmployeeStages}/${id}`,
  },
} as const;

export {
  WEEK_DAYS_SHORT,
  WEEK_DAY_NAMES,
  CALENDAR_MONTH_NAMES,
  WEEKLY_OFF_OPTIONS,
  createEmptyWeeklyOffDays,
  BULK_HOLIDAY_INITIAL_ROW_COUNT,
  HOLIDAY_YEAR_SELECTOR,
  HOLIDAY_IMPORT_DATE_FORMAT,
  formatHolidayImportDateExample,
  holidayImportDateExampleHint,
  holidayImportInvalidDateMessage,
  getHolidayListTemplateDateValidationError,
  getHolidayListTemplateDateNote,
  HOLIDAY_IMPORT_DATE_PATTERNS,
  HOLIDAY_IMPORT_CALENDAR_YEAR,
  HOLIDAY_CSV_DEFAULT_COLUMNS,
  HOLIDAY_CSV_HEADER_KEYS,
  HOLIDAY_BULK_UPLOAD,
  HOLIDAY_LIST_TEMPLATE,
} from './holidayConfig';

export { BANK_EDIT_WINDOW, BANK_DETAILS_MESSAGES } from './bankDetails';

export {
  AssetCategory,
  AssetFieldType,
  ASSET_BU_FILTER_NONE,
  ASSET_CATEGORIES,
  ASSET_CATEGORY_LABELS,
  ASSET_FIELD_TYPES,
  ASSET_FIELD_TYPE_LABELS,
  ASSET_FIELD_TYPES_WITH_OPTIONS,
  isAssetFieldTypeWithOptions,
} from './assets';

export {
  DOCUMENT_CATEGORY_CODES,
  DOCUMENT_TYPE_CODES,
  DOCUMENT_UPLOAD,
  DOCUMENT_ACCEPT_ALL,
  DOCUMENT_ACCEPT_IMAGES,
} from './documents';
export type { DocumentCategoryCode, DocumentTypeCode } from './documents';
